import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, query, where, orderBy } from "firebase/firestore";

const pad2 = (n) => String(n).padStart(2, "0");
const monthKeyFromDate = (d = new Date()) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;

const money = (v) =>
  new Intl.NumberFormat("es-DO", {
    style: "currency",
    currency: "DOP",
    maximumFractionDigits: 2,
  }).format(Number(v || 0));

const monthLabel = (key) => {
  // key = YYYY-MM
  const [y, m] = String(key || "").split("-");
  if (!y || !m) return "Mes";
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("es-DO", { month: "long", year: "numeric" });
};

const GlobalMiPagoCSS = () => (
  <style>{`
    .mp-wrap{ display:grid; gap:14px; }
    .mp-head{
      display:flex; align-items:center; justify-content:space-between;
      gap:12px; flex-wrap:wrap;
    }
    .mp-title{
      font-weight: 1000;
      font-size: 18px;
      color: rgba(15,23,42,.95);
      margin:0;
      text-transform: capitalize;
    }
    .mp-sub{ color: rgba(15,23,42,.65); font-size: 13px; }

    .mp-steps{
      display:grid; gap:12px;
      grid-template-columns: 1fr 1fr;
    }
    @media (max-width: 720px){
      .mp-steps{ grid-template-columns: 1fr; }
    }

    .step{
      border-radius: 18px;
      border: 1px solid rgba(37,99,235,.14);
      background: rgba(255,255,255,.70);
      box-shadow: 0 10px 25px rgba(2,6,23,.06);
      padding: 14px;
      position: relative;
      overflow: hidden;
      opacity: .70;
    }
    .step.enabled{ opacity: 1; }
    .step .k{ font-weight: 1000; color: rgba(15,23,42,.92); }
    .step .d{ margin-top: 6px; color: rgba(15,23,42,.65); font-size: 13px; line-height: 1.5; }

    .badge{
      display:inline-flex; align-items:center; gap:8px;
      font-weight: 950;
      padding: 8px 12px;
      border-radius: 999px;
      border: 1px solid rgba(37,99,235,.14);
      background: rgba(255,255,255,.72);
      box-shadow: 0 10px 25px rgba(2,6,23,.06);
      color: rgba(15,23,42,.9);
      white-space: nowrap;
    }

    .badge.ok{
      border-color: rgba(16,185,129,.35);
      background: rgba(16,185,129,.12);
      color: rgba(6,95,70,.95);
    }
    .badge.warn{
      border-color: rgba(245,158,11,.35);
      background: rgba(245,158,11,.12);
      color: rgba(124,45,18,.95);
    }
    .badge.off{
      border-color: rgba(148,163,184,.35);
      background: rgba(148,163,184,.10);
      color: rgba(15,23,42,.72);
    }

    .mp-grid{
      display:grid; gap:12px;
      grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
    }
    .mp-card{
      border-radius: 18px;
      border: 1px solid rgba(37,99,235,.14);
      background: rgba(255,255,255,.72);
      box-shadow: 0 10px 25px rgba(2,6,23,.06);
      padding: 12px;
    }
    .mp-k{ font-weight: 980; color: rgba(15,23,42,.92); }
    .mp-v{ margin-top: 6px; font-weight: 1000; font-size: 18px; color: rgba(15,23,42,.92); }
    .mp-h{ margin-top: 6px; color: rgba(15,23,42,.65); font-size: 13px; }

    .mp-tableWrap{ overflow-x:auto; }
    .mp-table{
      width:100%;
      border-collapse: collapse;
      border-radius: 14px;
      overflow:hidden;
      border: 1px solid rgba(37,99,235,.14);
      background: rgba(255,255,255,.72);
      box-shadow: 0 10px 25px rgba(2,6,23,.06);
      min-width: 720px;
    }
    .mp-table th, .mp-table td{
      padding: 10px;
      border-bottom: 1px solid rgba(37,99,235,.10);
      text-align:left;
      font-size: 14px;
      color: rgba(15,23,42,.9);
      vertical-align: top;
    }
    .mp-table th{
      font-weight: 950;
      background: linear-gradient(90deg, rgba(37,99,235,.10), rgba(6,182,212,.10));
    }

    .mp-empty{
      border-radius: 18px;
      padding: 12px;
      border: 1px dashed rgba(37,99,235,.25);
      background: rgba(255,255,255,.62);
      color: rgba(15,23,42,.70);
      font-weight: 900;
    }
  `}</style>
);

export default function MiPago({ userDocId, userData }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Mes actual (para cabecera)
  const currentMonthKey = useMemo(() => monthKeyFromDate(new Date()), []);

  useEffect(() => {
    if (!userDocId) return;

    setLoading(true);
    const qy = query(
      collection(db, "pagosSocios"),
      where("userId", "==", userDocId),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      qy,
      (snap) => {
        setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      () => {
        setRows([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [userDocId]);

  // Pago del mes actual (si existe)
  const current = useMemo(() => {
    const exact = rows.find((r) => r.monthKey === currentMonthKey);
    return exact || null;
  }, [rows, currentMonthKey]);

  const Propago = Boolean(current?.Propago);
  const Epago = Boolean(current?.Epago);

  const etapaProcesando = Propago && !Epago;
  const etapaPago = Epago;

  const total = useMemo(() => {
    const p1 = Number(current?.Pcomicionpublic || 0);
    const p2 = Number(current?.Pagconductor || 0);
    const p3 = Number(current?.pagproduc || 0);
    return p1 + p2 + p3;
  }, [current]);

  return (
    <div className="surface" style={{ padding: 14, display: "grid", gap: 14 }}>
      <GlobalMiPagoCSS />

      <div className="mp-wrap">
        <div className="mp-head">
          <div>
            <h3 className="mp-title">💵 Mi Pago — {monthLabel(currentMonthKey)}</h3>
            <div className="mp-sub">
              {userData?.nombre ? <>Socio: <b>{userData.nombre}</b> • </> : null}
              Monitorea el estado del pago de este mes.
            </div>
          </div>

          {loading ? (
            <span className="badge off">Cargando…</span>
          ) : current ? (
            etapaPago ? (
              <span className="badge ok">✅ Pago depositado</span>
            ) : etapaProcesando ? (
              <span className="badge warn">⏳ Procesando</span>
            ) : (
              <span className="badge off">⛔ Aún no disponible</span>
            )
          ) : (
            <span className="badge off">Sin registro del mes</span>
          )}
        </div>

        {/* Etapas */}
        <div className="mp-steps">
          <div className={`step ${etapaProcesando ? "enabled" : ""}`}>
            <div className="k">1) Procesando</div>
            <div className="d">
              Se activa cuando <b>Propago</b> está en <b>true</b> en <b>pagosSocios</b>.  
              Normalmente se verá unos días antes de terminar el mes.
            </div>
            <div style={{ marginTop: 10 }}>
              <span className={`badge ${etapaProcesando ? "warn" : "off"}`}>
                {etapaProcesando ? "⏳ Activo" : "Inhabilitado"}
              </span>
            </div>
          </div>

          <div className={`step ${etapaPago ? "enabled" : ""}`}>
            <div className="k">2) Pago</div>
            <div className="d">
              Se activa cuando <b>Epago</b> está en <b>true</b>.  
              Indica que el pago ya está en tu cuenta.
            </div>
            <div style={{ marginTop: 10 }}>
              <span className={`badge ${etapaPago ? "ok" : "off"}`}>
                {etapaPago ? "✅ Activo" : "Inhabilitado"}
              </span>
            </div>
          </div>
        </div>

        {/* Detalle */}
        {!current ? (
          <div className="mp-empty">
            No hay datos del mes actual todavía. (Cuando el sistema cree el registro del mes, aparecerá aquí.)
          </div>
        ) : (
          <>
            <div className="mp-grid">
              <div className="mp-card">
                <div className="mp-k">Comisión publicidad</div>
                <div className="mp-v">{money(current.Pcomicionpublic)}</div>
                <div className="mp-h">Campo: Pcomicionpublic</div>
              </div>

              <div className="mp-card">
                <div className="mp-k">Pago conductor</div>
                <div className="mp-v">{money(current.Pagconductor)}</div>
                <div className="mp-h">Campo: Pagconductor</div>
              </div>

              <div className="mp-card">
                <div className="mp-k">Comisión producto</div>
                <div className="mp-v">{money(current.pagproduc)}</div>
                <div className="mp-h">Campo: pagproduc</div>
              </div>

              <div className="mp-card">
                <div className="mp-k">Total</div>
                <div className="mp-v">{money(total)}</div>
                <div className="mp-h">{current.concepto ? `Concepto: ${current.concepto}` : "—"}</div>
              </div>
            </div>

            <div className="mp-tableWrap">
              <table className="mp-table">
                <thead>
                  <tr>
                    <th>Mes</th>
                    <th>Estado</th>
                    <th>Publicidad</th>
                    <th>Conductor</th>
                    <th>Producto</th>
                    <th>Total</th>
                    <th>Concepto</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 60).map((r) => {
                    const p1 = Number(r.Pcomicionpublic || 0);
                    const p2 = Number(r.Pagconductor || 0);
                    const p3 = Number(r.pagproduc || 0);
                    const t = p1 + p2 + p3;

                    const st = Boolean(r.Epago)
                      ? "Pago"
                      : Boolean(r.Propago)
                      ? "Procesando"
                      : "Inhabilitado";

                    return (
                      <tr key={r.id}>
                        <td style={{ fontWeight: 950, textTransform: "capitalize" }}>
                          {monthLabel(r.monthKey || "—")}
                        </td>
                        <td>{st}</td>
                        <td>{money(r.Pcomicionpublic)}</td>
                        <td>{money(r.Pagconductor)}</td>
                        <td>{money(r.pagproduc)}</td>
                        <td style={{ fontWeight: 950 }}>{money(t)}</td>
                        <td>{r.concepto || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="help" style={{ paddingTop: 8 }}>
                Mostrando hasta 60 meses (más reciente primero).
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
